import { Modal, Button, Typography, Tooltip, Popover, Form } from "antd"
import { useState, useEffect, useContext } from "react"
import { BugOutlined } from "@ant-design/icons"
import { AuthenticationContext } from "../../../../AuthenticationContext"
import dayjs, { Dayjs } from "dayjs"
import { Duration } from "../../../common/durationModel"
import { ScheduledEvent, RepeatingEventConfig, CrewMember, UpdateScheduledEventRequest } from "../../scheduleApi"
import { useUpdateScheduledEventMutation, useUpdateScheduledEventsRangeMutation, useUpdateFutureScheduledEventsMutation, useCreateScheduledEventByJobMutation } from "../../scheduleQueries"
import ScheduledEventsChangesModal, { ApplyChangesOption } from "./ScheduledEventsChangesModal"
import { EventFormValues, shouldAutoApplyFutureUpdate, shouldAllowRouteFutureUpdate } from "./manageScheduledEventModel"
import { useEventFormState } from "../../EventForm/useEventFormState"
import EventFormFields from "../../EventForm/EventFormFields"
import OpenJobLink from "../OpenJobLink"
import {
  parseRepeatingEventConfig,
  defaultRecurringFormValues,
  buildRepeatingEventConfig,
  buildTimingFields,
} from "../../EventForm/recurringEventFormHelpers"
import { deriveEndDateMode } from "../../EventForm/eventFormModel"
import { findCrewMemberByEmail } from "../../crewModel"

const { Text, Title } = Typography

type Props = {
  event: ScheduledEvent | null
  open: boolean
  onClose: () => void
  repeatingEventConfig?: RepeatingEventConfig | null
  initialDateOverride?: Dayjs | null
}

const ManageScheduledEvent = ({ open, onClose, event, repeatingEventConfig, initialDateOverride }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const [showChangesModal, setShowChangesModal] = useState(false)
  const [pendingPayload, setPendingPayload] = useState<UpdateScheduledEventRequest | null>(null)
  const [originalValues, setOriginalValues] = useState<EventFormValues | null>(null)
  const [allowFutureForRoute, setAllowFutureForRoute] = useState(false)

  const formState = useEventFormState({
    jobId: event?.jobId || 0,
  })

  const { form, formValues, setFormValues, areRecurringFieldsValid, crew } = formState

  const updateEventMutation = useUpdateScheduledEventMutation()
  const updateEventsRangeMutation = useUpdateScheduledEventsRangeMutation()
  const updateFutureEventsMutation = useUpdateFutureScheduledEventsMutation()
  const createEventMutation = useCreateScheduledEventByJobMutation()

  useEffect(() => {
    if (open && event) {
      const eventStartDate = dayjs(event.start)
      const eventEndDate = dayjs(event.end)
      const effectiveStartDate = initialDateOverride ?? eventStartDate
      const isPast = eventStartDate.isBefore(dayjs(), 'day')

      const crewMemberIdArray = event.crewMemberIds?.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id)) || []
      const crewMembersFromIds = crewMemberIdArray.map(id => crew?.find(member => member.id === id)).filter(Boolean) as CrewMember[]

      const crewLead = event.crewForemanEmail
        ? findCrewMemberByEmail(crew, event.crewForemanEmail)
        : null
      const crewLeadIdValue = crewLead?.id || null

      const foremanNotInMembers = crewLead && !crewMembersFromIds.some(m => m.id === crewLead.id)
      const crewMembers = foremanNotInMembers
        ? [crewLead, ...crewMembersFromIds]
        : crewMembersFromIds

      let workareaIdArray: number[] = []
      if (event.workAreaIds && event.workAreaIds !== '') {
        workareaIdArray = event.workAreaIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
      }

      const durationInMinutes = eventEndDate.diff(eventStartDate, 'minutes')
      const duration = Duration.minutes(durationInMinutes)

      const recurringValues = (repeatingEventConfig && !isPast)
        ? parseRepeatingEventConfig(repeatingEventConfig, eventStartDate)
        : defaultRecurringFormValues(eventStartDate)

      if (repeatingEventConfig && !isPast) {
        form.setFieldsValue({
          repeatUnit: recurringValues.repeatUnit,
          repeatCount: recurringValues.repeatCount,
          repeatPlace: recurringValues.repeatPlace,
          repeatQuarter: recurringValues.repeatQuarter,
          repeatMonth: recurringValues.repeatMonth,
        })
      } else {
        form.setFieldsValue({
          repeatUnit: 'none',
          repeatCount: 1,
          repeatPlace: ["1st"],
          repeatQuarter: [1],
          repeatMonth: [dayjs().month() + 1],
        })
      }

      setFormValues({
        isAllDay: event.allDay,
        selectedCrew: crewMembers,
        crewLeadId: crewLeadIdValue,
        sendCrewLeadNotifications: event.sendCrewLeadNotifications ?? false,
        selectedWorkareaIds: workareaIdArray,
        startDate: effectiveStartDate,
        startTime: eventStartDate,
        endDate: eventEndDate,
        endDateMode: deriveEndDateMode({ isAllDay: event.allDay, startDate: effectiveStartDate, endDate: eventEndDate }),
        estimatedDuration: duration,
        notes: event.notes || '',
        frozen: event.frozen,
        ...recurringValues,
      })

      setOriginalValues({
        ...recurringValues,
        startDate: eventStartDate,
        startTime: eventStartDate,
        estimatedDuration: duration,
        crewMemberIds: event.crewMemberIds || '',
        crewLeadId: crewLeadIdValue,
        workareaIds: workareaIdArray.join(','),
        notes: event.notes || '',
      })
    }
  }, [open, event, crew, repeatingEventConfig, form, initialDateOverride])

  const handleClose = () => {
    form.resetFields()
    onClose()
  }

  const isPastEvent = event ? dayjs(event.start).isBefore(dayjs(), 'day') : false

  const handleSave = async () => {
    try {
      if (!event?.id) {
        return
      }

      const { isAllDay, selectedCrew, crewLeadId, sendCrewLeadNotifications, selectedWorkareaIds, repeatUnit } = formValues

      const { actualStartDateTime, endDateTime, durationMinutes } = buildTimingFields(formValues)

      const crewMemberIds = selectedCrew.map(m => m.id).join(',')
      const workareaIds = selectedWorkareaIds.join(',')

      const updateRepeatingEventConfig = (!isPastEvent && repeatUnit !== 'none')
        ? buildRepeatingEventConfig(formValues)
        : null

      const isCreatingNewRepeatingPattern = repeatUnit !== 'none' && !isPastEvent && event.repeatingId === null

      const payload = {
        eventId: event.id,
        start: actualStartDateTime.toISOString(),
        end: endDateTime.toISOString(),
        dayStartTime: null,
        estimatedDurationInMinutes: durationMinutes,
        crewMemberIds,
        crewLeadId,
        workareaIds,
        routeId: event.routeId,
        repeatingEventConfig: updateRepeatingEventConfig,
        allDay: isAllDay,
        sendCrewLeadNotifications,
        notes: formValues.notes || null,
        frozen: formValues.frozen
      }

      if (isCreatingNewRepeatingPattern) {
        const createPayload = {
          jobId: event.jobId,
          start: actualStartDateTime.toISOString(),
          end: endDateTime.toISOString(),
          dayStartTime: null,
          estimatedDurationInMinutes: durationMinutes,
          crewMemberIds,
          crewLeadId,
          workareaIds,
          routeId: event.routeId,
          repeatingEventConfig: updateRepeatingEventConfig,
          hasApprovedWorkareas: false,
          allDay: isAllDay,
          sendCrewLeadNotifications,
          notes: formValues.notes || null,
          frozen: formValues.frozen
        }
        await createEventMutation.mutateAsync(createPayload)
        handleClose()
      } else if (isPastEvent || (event.routeId === null && event.repeatingId === null)) {
        await updateEventMutation.mutateAsync(payload)
        handleClose()
      } else {
        const currentValues: EventFormValues = {
          startDate: formValues.startDate,
          startTime: formValues.startTime,
          estimatedDuration: formValues.estimatedDuration,
          crewMemberIds,
          crewLeadId,
          workareaIds,
          repeatUnit: formValues.repeatUnit,
          repeatCount: formValues.repeatCount,
          repeatPlace: formValues.repeatPlace,
          repeatQuarter: formValues.repeatQuarter,
          repeatMonth: formValues.repeatMonth,
          repeatEndsOn: formValues.repeatEndsOn,
          notes: formValues.notes || ''
        }

        const autoApplyFutureUpdate = originalValues && shouldAutoApplyFutureUpdate(
          originalValues,
          currentValues,
          event.repeatingId !== null
        )

        if (autoApplyFutureUpdate) {
          await updateFutureEventsMutation.mutateAsync(payload)
          handleClose()
        } else {
          const allowRouteFutureUpdate = !!originalValues && shouldAllowRouteFutureUpdate(originalValues, currentValues)

          setPendingPayload(payload)
          setAllowFutureForRoute(allowRouteFutureUpdate)
          handleClose()
          setShowChangesModal(true)
        }
      }
    } catch (error) {
      console.error('Error saving event:', error)
    }
  }

  const handleApplyChanges = async (option: ApplyChangesOption, dateRange?: [Dayjs, Dayjs] | null) => {
    try {
      if (!pendingPayload) {
        return
      }

      if (option === 'this-event') {
        await updateEventMutation.mutateAsync(pendingPayload)
      } else if (option === 'selected-dates') {
        if (!dateRange) {
          return
        }
        await updateEventsRangeMutation.mutateAsync({
          updateRequest: pendingPayload,
          start: dateRange[0].toISOString(),
          end: dateRange[1].toISOString()
        })
      } else if (option === 'this-and-future') {
        await updateFutureEventsMutation.mutateAsync(pendingPayload)
      }

      setShowChangesModal(false)
      setPendingPayload(null)
    } catch (error) {
      console.error('Error applying changes:', error)
    }
  }

  const isFormValid = areRecurringFieldsValid()

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingRight: '24px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Title level={4} style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                  {event?.jobNumber && <Text type="secondary" style={{ marginRight: 8, fontSize: 'inherit' }}>{event.jobNumber}</Text>}
                  {event?.jobName}
                </Title>
                {event?.jobId ? <OpenJobLink jobId={event.jobId} /> : null}
              </div>
              {(() => {
                const fullText = `${event?.customerDisplayName || 'Unknown Client'} ${event?.customerAddress1 && event?.customerCity && event?.customerState && event?.customerZip ? `• ${event?.customerAddress1}, ${event?.customerCity}, ${event?.customerState} ${event?.customerZip}` : ''}`

                return (
                  <Tooltip title={fullText} placement="bottom">
                    <Text style={{
                      color: '#666',
                      fontSize: '16px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block'
                    }}>
                      {fullText}
                    </Text>
                  </Tooltip>
                )
              })()}
            </div>
          </div>
        }
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              {currentUser?.isSuperUser && (
                <Popover
                  trigger="click"
                  title="Event Debug"
                  content={
                    <pre style={{ maxHeight: 400, overflow: "auto", fontSize: 12 }}>
                      <code>{JSON.stringify(event, null, 2)}</code>
                    </pre>
                  }
                >
                  <Button type="text" icon={<BugOutlined />} size="small" />
                </Popover>
              )}
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <Button onClick={handleClose}>Cancel</Button>
              <Button type="primary" onClick={handleSave} disabled={!isFormValid}>
                Save
              </Button>
            </div>
          </div>
        }
        width={600}
      >
        <Form form={form} layout="vertical">
          <EventFormFields
            formState={formState}
            hideRepeatSelector={isPastEvent || event?.routeId !== null}
          />
        </Form>
      </Modal>
      <ScheduledEventsChangesModal
        open={showChangesModal}
        onClose={() => {
          setShowChangesModal(false)
          setPendingPayload(null)
          setAllowFutureForRoute(false)
        }}
        onConfirm={handleApplyChanges}
        event={event}
        allowFutureForRoute={allowFutureForRoute}
      />
    </>
  )
}

export default ManageScheduledEvent
